import { useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescriptionHidden,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { ScorecardDatePicker } from '@/components/custom/scorecard/scorecard-date-picker'
import { toLocalIsoDate } from '@/features/scorecard/scorecard-default-dates'
import { ScorecardTargetValueInput } from '@/components/custom/scorecard/scorecard-target-value-input'
import { useCreateScorecardActual } from '@/hooks/use-scorecard'
import { showSuccess } from '@/lib/toast'
import type { ScorecardKpiDto } from '@/services/scorecard.service'

export function ScorecardActualModal({
  open,
  onOpenChange,
  companyId,
  kpi,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  companyId: string
  kpi: ScorecardKpiDto
}) {
  const titleRef = useRef<HTMLHeadingElement>(null)
  const [value, setValue] = useState(kpi.actual?.value ?? 0)
  const [asOfDate, setAsOfDate] = useState(() => toLocalIsoDate())
  const mutation = useCreateScorecardActual(companyId)
  const dataType =
    kpi?.metricLabel.dataType ?? kpi?.metricLabel.metricType?.dataType

  const submit = async () => {
    if (!Number.isFinite(value) || !asOfDate) return
    try {
      await mutation.mutateAsync({ kpiId: kpi.id, data: { value, asOfDate } })
      showSuccess('Actual recorded')
      onOpenChange(false)
    } catch {
      // Axios displays the error; keep the modal open for correction.
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md gap-0 p-0"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          titleRef.current?.focus()
        }}
      >
        <DialogHeader className="border-b border-primary/10 px-5 py-4">
          <DialogTitle
            ref={titleRef}
            tabIndex={-1}
            className="focus:outline-none"
          >
            Record actual
          </DialogTitle>
          <DialogDescriptionHidden>
            Record a dated actual for{' '}
            {kpi.displayName ?? kpi.metricLabel.labeledAs}.
          </DialogDescriptionHidden>
        </DialogHeader>
        <div className="grid gap-4 px-5 py-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="scorecard-actual-value">Actual value</Label>
            <ScorecardTargetValueInput
              id="scorecard-actual-value"
              value={value}
              onChange={setValue}
              dataType={dataType}
              unitLabel={kpi.unitLabel}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="scorecard-actual-date">As-of date</Label>
            <ScorecardDatePicker
              id="scorecard-actual-date"
              value={asOfDate}
              onChange={setAsOfDate}
            />
          </div>
        </div>
        <DialogFooter className="border-t border-primary/10 px-5 py-4">
          <Button
            type="button"
            variant="outline-muted"
            disabled={mutation.isPending}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={
              mutation.isPending || !Number.isFinite(value) || !asOfDate
            }
            onClick={() => void submit()}
          >
            {mutation.isPending ? 'Saving...' : 'Record actual'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
